import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Clock, AlertTriangle, Bot, User, Trash2, Pencil, Link2, Calendar } from 'lucide-react';
import { etiquetaDeEstadoDeTarea, TASK_TYPE_LABELS } from './constants';
import { textoDeBloqueo } from '@/lib/dependenciasPendientes';

function assigneeLabel(assignee) {
  if (!assignee) return 'Sin asignar';
  if (assignee.kind === 'agent') return assignee.name || assignee.agent_id || assignee.id || 'Agente';
  return assignee.name || assignee.id || '—';
}

function formatDateRange(task) {
  const parts = [];
  if (task.planned_start) parts.push(`Inicio ${task.planned_start}`);
  if (task.planned_end) parts.push(`Fin ${task.planned_end}`);
  return parts;
}

export default function TaskCard({
  task,
  pendingBlockers,
  phases,
  taskStatuses,
  onStatusChange,
  onPhaseChange,
  onOpen,
  onEdit,
  onDelete,
  onLogHours,
  logTaskId,
  logMinutes,
  logNota,
  setLogTaskId,
  setLogMinutes,
  setLogNota,
}) {
  const blockers = pendingBlockers || [];
  const isAgent = task.assignee?.kind === 'agent';
  const dateParts = formatDateRange(task);
  const suggested = task.dates_confirmed === false;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-2 text-[11px] space-y-1.5"
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-start justify-between gap-1">
        {/* El titulo abre la **pagina** de la tarea, que es donde se lee. El lapiz sigue
            abriendo el dialogo, que es donde se edita. */}
        <button type="button" className="font-medium text-zinc-800 leading-snug text-left flex-1 hover:text-brand-primary" onClick={() => onOpen?.(task)}>
          {task.title}
        </button>
        {/* **Tres pixeles separaban editar de borrar**, y borrar se lleva las horas cargadas.
            Ahora hay aire entre los dos y el de borrar pide confirmacion diciendo que se lleva. Los
            dos tienen `data-testid`: el borrado sin confirmacion era tambien el unico boton
            destructivo de este componente sin gancho de test. */}
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" className="text-zinc-400 hover:text-brand-primary"
            onClick={() => onEdit?.(task)} title="Editar la tarea"
            aria-label="Editar la tarea" data-testid={`task-edit-${task.task_id}`}>
            <Pencil className="w-3 h-3" />
          </button>
          <button type="button" className="text-zinc-300 hover:text-brand-danger"
            onClick={() => onDelete?.(task)} title="Eliminar la tarea y sus horas cargadas"
            aria-label="Eliminar la tarea" data-testid={`task-delete-${task.task_id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* **Decia solo el nombre de la predecesora**, y la parte que importa es la otra: *que* le
          falta a esa predecesora, porque es lo unico que dice como se destraba. El servidor manda
          las dos en cada bloqueo —un `finish_to_start` pide que termine, un `start_to_start` solo
          que arranque— y la tarjeta se quedaba con la mitad. La frase la arma `unaLineaDeBloqueo`,
          que es la misma que usan la ficha y la bandeja: tres pantallas, una declaracion. */}
      {blockers.length > 0 && (
        <div
          className="flex items-start gap-1 text-[9px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded-sm"
          title="El bloqueo avisa, no impide: se puede avanzar igual si hace falta solaparse."
          data-testid={`task-blocked-${task.task_id}`}
        >
          <AlertTriangle className="w-2.5 h-2.5 shrink-0 mt-[1px]" />
          <span>Bloqueada — {textoDeBloqueo(task)}</span>
        </div>
      )}

      {dateParts.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap text-[9px] text-zinc-500">
          <Calendar className="w-2.5 h-2.5" />
          {dateParts.join(' · ')}
          {suggested && (
            <Badge variant="outline" className="text-[8px] px-1 py-0 rounded-sm border-dashed">sugerido</Badge>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 flex-wrap">
        <select
          value={task.status || 'todo'}
          onChange={(e) => onStatusChange(task.task_id, e.target.value)}
          className="border border-zinc-200 rounded-sm px-1 py-0.5 text-[10px] bg-white"
          data-testid={`task-status-${task.task_id}`}
        >
          {(taskStatuses || []).map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        {phases?.length > 0 && (
          <select
            value={task.phase || ''}
            onChange={(e) => onPhaseChange(task.task_id, e.target.value)}
            className="border border-zinc-200 rounded-sm px-1 py-0.5 text-[10px] bg-white"
          >
            <option value="">Sin fase</option>
            {phases.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        )}
        {blockers.length > 0 && (
          <Link2 className="w-2.5 h-2.5 text-amber-600" title="Tiene dependencias" />
        )}
      </div>

      <div className="flex items-center gap-2 text-[9px] text-zinc-500">
        <Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm">{TASK_TYPE_LABELS[task.task_type] || task.task_type}</Badge>
        <span className="flex items-center gap-0.5">
          {isAgent ? <Bot className="w-2.5 h-2.5" /> : <User className="w-2.5 h-2.5" />}
          {assigneeLabel(task.assignee)}
        </span>
        {task.estimate_hours != null && <span>est {task.estimate_hours}h</span>}
        {task.consumed_hours > 0 && <span>real {task.consumed_hours}h</span>}
      </div>

      {/* Un indicio de que hay entrega, no la entrega. Se leia aca a 9 px, recortada a 300
          caracteres, en una columna de 200 px — y solo si la tarea seguia asignada a un
          agente, asi que al reasignarla a una persona desaparecia. Ahora se lee en la pagina. */}
      {task.output_text && (
        <button type="button" onClick={() => onOpen?.(task)}
          className="text-[10px] text-brand-primary hover:underline text-left">
          Ver la entrega del agente ({task.output_text.length} caracteres)
        </button>
      )}

      {logTaskId === task.task_id ? (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Input
              autoFocus type="number" step="0.25" min="0" value={logMinutes}
              onChange={(e) => setLogMinutes(e.target.value)}
              placeholder="horas" className="h-6 text-[10px] rounded-sm w-16"
            />
            {/* La nota no es un extra: sin ella el numero no se puede auditar despues. Por eso
                el OK no se habilita hasta que este, y no un aviso que se pueda ignorar. */}
            <Input
              value={logNota || ''}
              onChange={(e) => setLogNota(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (logNota || '').trim()) onLogHours(task.task_id); }}
              placeholder="en que se fueron" className="h-6 text-[10px] rounded-sm flex-1"
              maxLength={500}
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm" className="h-6 text-[9px] rounded-sm px-2"
              disabled={!logMinutes || !(logNota || '').trim()}
              onClick={() => onLogHours(task.task_id)}
            >
              OK
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[9px] rounded-sm px-1" onClick={() => { setLogTaskId(null); setLogMinutes(''); setLogNota(''); }}>X</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { setLogTaskId(task.task_id); setLogMinutes(''); setLogNota(''); }}
          className="text-[9px] text-zinc-400 hover:text-brand-primary flex items-center gap-1"
        >
          <Clock className="w-2.5 h-2.5" /> Cargar horas
        </button>
      )}
    </div>
  );
}
